import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useBlurredTransferPlayers } from './use-blurred-transfer-players';
import StepHeader from '@components/quiz/shared/StepHeader';
import CtaButton from '@components/quiz/shared/CtaButton';
import RatingGauge, {
  ANIMATION_DURATION_MS
} from '@components/quiz/shared/RatingGauge';
import PitchPlayer from '@components/manual-team-entry/pitch-player';
import { useGytr } from '@components/quiz/GytrProvider';
import { fetchSquadPlayers } from '@lib/api/gytr-portraits';
import type { Player } from '@api-service/players';
import type { SanityGytrTransferBlock } from '@lib/sanity/types';

type GytrTransferStepProps = SanityGytrTransferBlock & {
  onNext: () => void;
};

const GAUGE_SIZE = 132;

/**
 * Holds the delta's place between the gauges while there is no delta to show.
 * Something has to occupy the gap or the two gauges close up and the row
 * reflows the moment the suggestion lands.
 */
const PENDING_DELTA_PLACEHOLDER = '···';

/** Stand-in for a portrait that has not resolved, on either side of a card. */
const PortraitPlaceholder = () => (
  <div className="h-16 w-16 rounded-full bg-neutral-100" />
);

/**
 * The rule-and-arrowhead under an OUT or IN label, drawn in `currentColor` so
 * it takes the colour of the label above it and the pair reads as one mark.
 * Mirrored rather than redrawn for the return leg, so both arrows are the same
 * shape pointing opposite ways.
 */
const DirectionArrow = ({ reversed = false }: { reversed?: boolean }) => (
  <svg
    viewBox="0 0 40 8"
    className={`h-2 w-10 ${reversed ? 'scale-x-[-1]' : ''}`}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M1 4h34" />
    <path d="m31.5 1 3.5 3-3.5 3" />
  </svg>
);

/**
 * One side of the swap: the word, and an arrow beneath it carrying the word's
 * colour. The colour is the whole signal here, so the label is set in it
 * directly rather than sat on a tinted pill.
 */
const TransferDirection: FC<{
  label: string;
  className: string;
  reversed?: boolean;
}> = ({ label, className, reversed = false }) => (
  <div className={`flex flex-col items-center gap-0.5 ${className}`}>
    <span className="font-heading text-[10px] font-bold uppercase leading-none tracking-wide">
      {label}
    </span>
    <DirectionArrow reversed={reversed} />
  </div>
);

/** What one card on this step needs to render. */
type TransferCardModel = {
  /** The squad player being moved on. Absent while the solve is pending. */
  outPlayerId: string | undefined;
  /** The predicted-points gain, or null while there is none to promise. */
  uplift: number | null;
};

type TransferCardProps = {
  outPlayer: Player | null;
  /**
   * The decoy whose blurred portrait stands in for the IN recommendation. Not
   * the recommendation itself, which never reaches the client.
   */
  inPlayer: Player | null;
  uplift: number | null;
  outLabel: string | undefined;
  inLabel: string | undefined;
  upliftLabel: string | undefined;
  pendingUpliftLabel: string | undefined;
};

/**
 * One locked transfer: the OUT player named and shown, the IN recommendation
 * blurred out and never revealed, with the predicted-points uplift as the free
 * sample.
 */
const TransferCard: FC<TransferCardProps> = ({
  outPlayer,
  inPlayer,
  uplift,
  outLabel,
  inLabel,
  upliftLabel,
  pendingUpliftLabel
}) => (
  <div className="flex items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-white px-4 py-4">
    <div className="flex shrink-0 scale-90 items-center">
      {outPlayer ? (
        <PitchPlayer
          name={outPlayer.displayName}
          teamCode={outPlayer.team.shortName}
          imageUrl={outPlayer.imageUrl}
          isCompact
        />
      ) : (
        <PortraitPlaceholder />
      )}
    </div>

    {/* The arrow's 40px sets this column's width, within a couple of pixels of
        the pills it replaces, so the cards around it do not need resizing. */}
    <div className="flex shrink-0 flex-col items-center gap-2">
      <TransferDirection
        label={outLabel ?? 'OUT'}
        className="text-danger-600"
      />
      <TransferDirection
        label={inLabel ?? 'IN'}
        className="text-success-600"
        reversed
      />
    </div>

    {/* The IN side: a real player token with every identifying part hazed.
        Decorative, and hidden from assistive tech — the player shown is a
        decoy, so announcing the name would offer a transfer that is not on
        the table. The uplift beside it is the honest part. */}
    <div
      aria-hidden="true"
      data-cy="transfer-in-blurred"
      className="flex shrink-0 scale-90 items-center"
    >
      {inPlayer ? (
        <PitchPlayer
          name={inPlayer.displayName}
          teamCode={inPlayer.team.shortName}
          imageUrl={inPlayer.imageUrl}
          isBlurred
          isCompact
        />
      ) : (
        <PortraitPlaceholder />
      )}
    </div>

    <div className="shrink-0 text-right">
      {uplift === null ? (
        // Announced rather than silently swapped, so the figure arriving
        // is not the first a screen reader hears of it.
        <p
          role="status"
          className="font-body text-[11px] leading-tight text-neutral-400"
        >
          {pendingUpliftLabel ?? 'Working it out'}
        </p>
      ) : (
        <>
          <p className="font-heading text-xl font-extrabold text-success-600">
            +{uplift.toFixed(1)}
          </p>
          <p className="font-body text-[11px] leading-tight text-neutral-400">
            {upliftLabel ?? 'pred. pts / GW'}
          </p>
        </>
      )}
    </div>
  </div>
);

/**
 * Step 5: transfer improvement (spec 5.5). Before/after gauges crossing
 * colour bands, and up to three locked transfers (FFH-2382) — each OUT player
 * named and shown, each IN recommendation locked behind a padlock and never
 * revealed, with the predicted-points uplift as the free sample.
 *
 * One card is the common case, not a degraded one: the solve only takes a
 * second or third transfer where it beats the -4 hit, so the layout is built
 * to look deliberate at one and simply stack when there are more.
 */
const GytrTransferStep: FC<GytrTransferStepProps> = ({
  header,
  gauge,
  nowLabel,
  withFfhLabel,
  transferSectionLabel,
  transferSectionLabelPlural,
  outLabel,
  inLabel,
  upliftLabel,
  pendingUpliftLabel,
  pendingNote,
  trajectoryBands,
  ctaText,
  loginLinkText,
  loginUrl,
  onNext
}) => {
  const { state } = useGytr();
  const [fetchedPortraits, setFetchedPortraits] = useState<Map<string, Player>>(
    () => new Map()
  );
  /**
   * Ids already asked for, so a player the fetch cannot resolve is not asked
   * for again on every render. Held in a ref rather than state because wanting
   * it to survive a render is the whole of its job; putting it in the effect's
   * dependencies would be the loop it exists to prevent.
   */
  const requestedPortraits = useRef<Set<string>>(new Set());

  // Every figure on this step below the rating itself is derived from a
  // transfer that does not exist yet, so each is withheld rather than
  // defaulted. A zero here is indistinguishable from a solver that found
  // nothing worth doing, which is the one thing this step must not say while
  // it is still looking.
  const isPending = state.isSuggestionPending;

  const nowValue = state.teamRating;
  const withValue = isPending
    ? null
    : state.potentialRating ?? state.teamRating;
  const gaugeDelta =
    nowValue !== null && withValue !== null
      ? Math.max(0, Math.round(withValue - nowValue))
      : 0;

  const cards = useMemo<TransferCardModel[]>(() => {
    // Nothing is named yet, so the step holds one placeholder card. Naming a
    // player under a transfer that does not exist is exactly the claim this
    // step must not make while it is still looking.
    if (isPending) return [{ outPlayerId: undefined, uplift: null }];

    if (state.topTransfers.length > 0) {
      return state.topTransfers.map((transfer) => ({
        outPlayerId: transfer.outPlayerId,
        uplift: transfer.pointsUplift
      }));
    }

    // The solve named nothing. Fall back to the AI's weakest starter with the
    // gauge delta standing in for an uplift, exactly as this step has always
    // done — one card, never three, because there is only one such opinion.
    return [
      { outPlayerId: state.aiSellPlayerId ?? undefined, uplift: gaugeDelta }
    ];
  }, [isPending, state.topTransfers, state.aiSellPlayerId, gaugeDelta]);

  const outPlayerIds = useMemo(
    () =>
      cards
        .map((card) => card.outPlayerId)
        .filter((id): id is string => Boolean(id)),
    [cards]
  );

  useEffect(() => {
    // Every OUT player is a squad member, so the prefetched portraits cover
    // them in the ordinary case and this fetches nothing. The fallback is for
    // a prefetch that has not landed or did not include somebody.
    const missing = outPlayerIds.filter(
      (id) =>
        !state.playerPortraits?.has(id) && !requestedPortraits.current.has(id)
    );
    if (missing.length === 0) return;

    for (const id of missing) requestedPortraits.current.add(id);

    // One request for the whole step rather than one per card.
    //
    // Deliberately not cancelled on cleanup. A portrait is reference data
    // keyed by player id, so an answer arriving after a re-render is still
    // the right answer and merging it can only improve what is on screen.
    // Dropping it would strand the placeholder: the ids are already marked as
    // requested, so nothing would ask for them a second time.
    fetchSquadPlayers(missing)
      .then((fetched) => {
        setFetchedPortraits((previous) => new Map([...previous, ...fetched]));
      })
      .catch(() => {
        // Unmarked so a later render may try again. Left marked, one failed
        // request would hold the grey circle for the rest of the session.
        for (const id of missing) requestedPortraits.current.delete(id);
      });
  }, [outPlayerIds, state.playerPortraits]);

  // The blurred faces on the IN side of each card, excluding whoever is being
  // transferred out (FFH-2409).
  const blurredInPlayers = useBlurredTransferPlayers(
    outPlayerIds,
    cards.length
  );

  const portraitFor = (outPlayerId: string | undefined): Player | null => {
    if (!outPlayerId) return null;
    return (
      state.playerPortraits?.get(outPlayerId) ??
      fetchedPortraits.get(outPlayerId) ??
      null
    );
  };

  const sectionLabel =
    cards.length > 1
      ? transferSectionLabelPlural ??
        transferSectionLabel ??
        'THE TRANSFERS THAT DO IT'
      : transferSectionLabel ?? 'THE TRANSFER THAT DOES IT';

  const bands = gauge?.bands?.map((band) => ({
    maxValue: band.maxValue,
    colour: band.colour
  }));

  // Post-GW1 only: a cohort-pattern trajectory line keyed to the user's real
  // percentile (spec 5.5). Pre-GW1 carries no bands, so this stays hidden.
  const percentile = state.gw1Performance?.percentile;
  const trajectoryLine =
    trajectoryBands && trajectoryBands.length > 0 && percentile != null
      ? [...trajectoryBands]
          .sort((a, b) => a.maxPercentile - b.maxPercentile)
          .find((band) => percentile <= band.maxPercentile)?.line
      : undefined;

  return (
    <section className="flex flex-col items-center px-6 pb-40 pt-6">
      <div className="w-full max-w-sm">
        <StepHeader
          eyebrow={header?.eyebrow}
          accentHeading={header?.heading}
          subtext={header?.subtext}
        />

        {/* Before / after gauges */}
        <div className="mt-8 flex items-center justify-center gap-2">
          <RatingGauge
            value={nowValue}
            bands={bands}
            subtext={nowLabel ?? 'NOW'}
            size={GAUGE_SIZE}
          />
          <div
            className={`flex flex-col items-center ${
              // Grey rather than green while pending: the colour is the claim
              // that the number is an improvement, and there is no number yet.
              isPending ? 'text-neutral-300' : 'text-success-600'
            }`}
          >
            <span className="font-heading text-lg font-extrabold">
              {isPending ? PENDING_DELTA_PLACEHOLDER : `+${gaugeDelta}`}
            </span>
            <span aria-hidden="true" className="text-xl">
              →
            </span>
          </div>
          <RatingGauge
            value={withValue}
            bands={bands}
            subtext={withFfhLabel ?? 'WITH FFH'}
            size={GAUGE_SIZE}
            delayMs={ANIMATION_DURATION_MS}
          />
        </div>

        {/* The locked transfers */}
        <p className="mt-8 text-center font-heading text-xs font-bold uppercase tracking-widest text-neutral-500">
          {sectionLabel}
        </p>

        <div className="mt-3 flex flex-col gap-3">
          {cards.map((card, index) => (
            <TransferCard
              // The list is short, ordered by the server and only ever
              // replaced wholesale, so position is a stable identity here.
              // The OUT player id would be the better key but is absent while
              // the solve is pending, which is the one state that renders a
              // card without one.
              key={card.outPlayerId ?? `pending-${String(index)}`}
              outPlayer={portraitFor(card.outPlayerId)}
              inPlayer={blurredInPlayers[index] ?? null}
              uplift={card.uplift}
              outLabel={outLabel}
              inLabel={inLabel}
              upliftLabel={upliftLabel}
              pendingUpliftLabel={pendingUpliftLabel}
            />
          ))}
        </div>

        {isPending && (
          <p className="mt-3 text-center font-body text-xs text-neutral-500">
            {pendingNote ??
              'Your rating is final. We are still working out the transfer that improves it most.'}
          </p>
        )}

        {trajectoryLine && (
          <p className="mt-6 rounded-xl bg-neutral-50 px-4 py-3 text-center font-body text-sm text-neutral-600">
            {trajectoryLine}
          </p>
        )}
      </div>

      {/* CTA + login link anchored to the bottom of the viewport so they
          stay available however far the user scrolls (paywall pattern). */}
      <div
        className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white px-6 pt-3 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto w-full max-w-sm">
          <CtaButton onClick={onNext} dataCy="gytr-transfer-cta">
            {ctaText ?? 'Sign up to see all transfers'}
          </CtaButton>
          {loginLinkText && (
            <a
              href={loginUrl ?? '/auth/login'}
              className="mt-4 block w-full text-center font-body text-sm font-bold text-primary-500 transition-colors hover:text-primary-600"
            >
              {loginLinkText}
            </a>
          )}
        </div>
      </div>
    </section>
  );
};

export default GytrTransferStep;
